import { approvalNeedsSixDigitPin, validatePin } from '@didto/domain';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmSheet,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  Notice,
  PageHeader,
  Select,
  Sheet,
  StatusChip,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { useSession } from '@/app/session';
import { useCommand } from '@/app/useCommand';
import type { Row } from '@/lib/db';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { supabase } from '@/lib/supabase';
import './employees.css';

type Employee = Pick<
  Row<'employee'>,
  'id' | 'display_name' | 'code' | 'status' | 'auth_user_id' | 'pin_set_at'
>;
type Role = Pick<Row<'role'>, 'id' | 'key' | 'name' | 'is_system'>;
export function EmployeesPage() {
  const s = useSession();
  const locationId = s.me?.active_location_id ?? '';
  const canManage = s.can('employee.manage');
  const cmd = useCommand([['employees'], ['assignments']]);
  const [editing, setEditing] = useState<Employee | 'new' | null>(null);
  const [pinFor, setPinFor] = useState<Employee | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const employees = useQuery({
    queryKey: ['employees'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee')
        .select('id, display_name, code, status, auth_user_id, pin_set_at')
        .order('display_name');
      if (error) throw error;
      return data;
    },
  });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('role')
        .select('id, key, name, is_system')
        .order('sort');
      if (error) throw error;
      return data;
    },
  });
  const assignments = useQuery({
    queryKey: ['assignments', locationId],
    enabled: !!locationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee_location_role')
        .select('employee_id, location_id, role_id')
        .eq('location_id', locationId);
      if (error) throw error;
      return data;
    },
  });

  const rolePermissions = useQuery({
    queryKey: ['role-permissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('role_permission')
        .select('role_id, permission_key');
      if (error) throw error;
      return data;
    },
  });
  /** ADR-023: whoever can approve money or owner-level actions sets a 6-digit PIN. */
  const sixDigitsFor = (roleId: string | null | undefined) => {
    if (!roleId) return false;
    if (roles.data?.find((r) => r.id === roleId)?.key === 'OWNER') return true;
    return approvalNeedsSixDigitPin(
      (rolePermissions.data ?? []).filter((x) => x.role_id === roleId).map((x) => x.permission_key),
    );
  };

  const roleOf = (e: Employee) => {
    const a = assignments.data?.find((x) => x.employee_id === e.id);
    return roles.data?.find((r) => r.id === a?.role_id) ?? null;
  };
  /** ADR-025: an owner account is changed only by an owner, from the signed-in account. */
  const isOwnerAccount = (e: Employee) => (s.me?.owner_employee_ids ?? []).includes(e.id);
  /** The roles this session may give: OWNER only from an owner's signed-in account. */
  const grantableRoles = (roles.data ?? []).filter((r) => r.key !== 'OWNER' || s.canActOnOwners);
  const list = (employees.data ?? []).filter((e) => showArchived || e.status !== 'archived');
  // the role reads: without them every row would read "no role here"
  const roleReads = [roles, assignments, rolePermissions];
  const roleReadError = roleReads.find((q) => q.isError)?.error;

  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Staff"
        title="Employees"
        subtitle="Everyone who works at the café: their role at each location and the PIN they use on the tablets."
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setEditing('new')} data-testid="new-employee">
              New employee
            </Button>
          ) : undefined
        }
      />
      <div className="d-spread">
        <Checkbox checked={showArchived} onChange={setShowArchived} label="Show archived" />
      </div>
      {canManage && !s.canActOnOwners && (
        <Notice tone="info" testId="owner-accounts-note">
          Owner accounts are changed only by an owner, signed in with their own account.
        </Notice>
      )}
      {roleReadError && (
        <ErrorState
          title="Could not load the roles"
          message={errorText(roleReadError)}
          onRetry={() => {
            for (const q of roleReads) if (q.isError) void q.refetch();
          }}
        />
      )}
      <div className="d-list" data-testid="employee-list">
        {employees.isLoading && <Loading rows={4} label="Loading employees…" />}
        {employees.isError && (
          <ErrorState
            title="Could not load the employees"
            message={errorText(employees.error)}
            onRetry={() => void employees.refetch()}
          />
        )}
        {list.map((e) => {
          const role = roleOf(e);
          return (
            <div key={e.id} className="d-row" data-testid={`employee-row-${e.id}`}>
              <div className="d-row__main">
                <div className="d-row__title">
                  <span>{e.display_name}</span>{' '}
                  {e.code && <span className="d-mono d-faint">{e.code}</span>}
                </div>
                <div className="d-row__meta d-inline">
                  {role ? <Badge>{role.name}</Badge> : <Badge tone="warning">no role here</Badge>}
                  {e.pin_set_at ? (
                    <StatusChip tone="success">PIN set</StatusChip>
                  ) : (
                    <StatusChip tone="warning">no PIN</StatusChip>
                  )}
                  {e.auth_user_id && <Badge tone="info">login linked</Badge>}
                  {e.status === 'suspended' && <Badge tone="danger">suspended</Badge>}
                  {e.status === 'archived' && <Badge>archived</Badge>}
                  {isOwnerAccount(e) && !s.canActOnOwners && (
                    <Badge outline testId={`owner-protected-${e.id}`}>
                      owner account
                    </Badge>
                  )}
                </div>
              </div>
              {s.canManageEmployee(e.id) && e.status !== 'archived' && (
                <div className="d-actions">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPinFor(e)}
                    data-testid={`set-pin-${e.id}`}
                  >
                    {pinAction(e)}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditing(e)}
                    data-testid={`edit-employee-${e.id}`}
                  >
                    Edit
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        {employees.isSuccess && list.length === 0 && <EmptyState title="No employees yet" />}
      </div>

      {editing && (
        <EmployeeSheet
          employee={editing === 'new' ? null : editing}
          roles={grantableRoles}
          currentRoleId={editing !== 'new' ? (roleOf(editing)?.id ?? '') : ''}
          sixDigitsFor={sixDigitsFor}
          isSelf={editing !== 'new' && editing.id === s.me?.employee?.id}
          onClose={() => setEditing(null)}
          onSave={async (v) => {
            if (editing === 'new') {
              await cmd.mutateAsync({
                kind: 'employee.create',
                payload: {
                  display_name: v.display_name,
                  code: v.code || null,
                  ...(v.role_id ? { role_id: v.role_id, location_id: locationId } : {}),
                  ...(v.pin ? { pin: v.pin } : {}),
                },
              });
            } else {
              await cmd.mutateAsync({
                kind: 'employee.update',
                payload: {
                  employee_id: editing.id,
                  display_name: v.display_name,
                  code: v.code || null,
                },
              });
              const current = roleOf(editing)?.id ?? '';
              if (v.role_id && v.role_id !== current)
                await cmd.mutateAsync({
                  kind: 'employee.assign_role',
                  payload: { employee_id: editing.id, location_id: locationId, role_id: v.role_id },
                });
              if (!v.role_id && current)
                await cmd.mutateAsync({
                  kind: 'employee.unassign_role',
                  payload: { employee_id: editing.id, location_id: locationId },
                });
            }
            setEditing(null);
          }}
          onSuspend={
            editing !== 'new' && editing.status === 'active'
              ? async (reason) => {
                  await cmd.mutateAsync({
                    kind: 'employee.suspend',
                    payload: { employee_id: editing.id, reason },
                  });
                  setEditing(null);
                }
              : undefined
          }
          onReactivate={
            editing !== 'new' && editing.status === 'suspended'
              ? async () => {
                  await cmd.mutateAsync({
                    kind: 'employee.reactivate',
                    payload: { employee_id: editing.id },
                  });
                  setEditing(null);
                }
              : undefined
          }
          onArchive={
            editing !== 'new'
              ? async (reason) => {
                  await cmd.mutateAsync({
                    kind: 'employee.archive',
                    payload: { employee_id: editing.id, reason },
                  });
                  setEditing(null);
                }
              : undefined
          }
          onLink={
            editing !== 'new'
              ? async (email) => {
                  await cmd.mutateAsync({
                    kind: 'employee.link_login',
                    payload: { employee_id: editing.id, email },
                  });
                  setEditing(null);
                }
              : undefined
          }
          onUnlink={
            editing !== 'new' && editing.auth_user_id
              ? async () => {
                  await cmd.mutateAsync({
                    kind: 'employee.unlink_login',
                    payload: { employee_id: editing.id },
                  });
                  setEditing(null);
                }
              : undefined
          }
        />
      )}
      {pinFor && (
        <PinSheet
          employee={pinFor}
          sixDigits={sixDigitsFor(roleOf(pinFor)?.id)}
          onClose={() => setPinFor(null)}
          onSave={async (pin) => {
            await cmd.mutateAsync({
              kind: 'employee.set_pin',
              payload: { employee_id: pinFor.id, pin },
            });
            setPinFor(null);
          }}
        />
      )}
    </div>
  );
}

function EmployeeSheet(props: {
  employee: Employee | null;
  roles: Role[];
  currentRoleId: string;
  /** The PIN rule for a role: 6 digits for roles that approve money or owner-level actions. */
  sixDigitsFor: (roleId: string) => boolean;
  isSelf: boolean;
  onClose: () => void;
  onSave: (v: {
    display_name: string;
    code: string;
    role_id: string;
    pin: string;
  }) => Promise<void>;
  onSuspend?: (reason: string) => Promise<void>;
  onReactivate?: () => Promise<void>;
  onArchive?: (reason: string) => Promise<void>;
  onLink?: (email: string) => Promise<void>;
  onUnlink?: () => Promise<void>;
}) {
  const { employee } = props;
  // ADR-037: every change here is a command; none starts while the server cannot be reached
  const online = useOnline();
  const [name, setName] = useState(employee?.display_name ?? '');
  const [code, setCode] = useState(employee?.code ?? '');
  const [roleId, setRoleId] = useState(props.currentRoleId);
  const [pin, setPin] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const sixDigits = props.sixDigitsFor(roleId);
  const pinCheck = validatePin(pin, { sixDigits });
  const pinError = pin && !pinCheck.ok ? (pinCheck as { error: string }).error : null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (pinError) return;
    setBusy(true);
    try {
      await props.onSave({
        display_name: name.trim(),
        code: code.trim().toUpperCase(),
        role_id: roleId,
        pin,
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Sheet
        open
        onClose={props.onClose}
        title={employee ? `Edit ${employee.display_name}` : 'New employee'}
      >
        <form onSubmit={(e) => void submit(e)} className="d-stack">
          <Field label="Display name">
            {(id) => (
              <Input
                id={id}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={80}
                autoFocus
                data-testid="employee-name"
              />
            )}
          </Field>
          <Field label="Short code (optional)" help="Shown on tickets and reports, e.g. JM">
            {(id) => (
              <Input
                id={id}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={12}
                className="d-mono"
                data-testid="employee-code"
              />
            )}
          </Field>
          <Field
            label="Role at this location"
            help={
              props.isSelf
                ? 'You cannot remove your own owner role while you are the last owner.'
                : employee && sixDigits && !props.sixDigitsFor(props.currentRoleId)
                  ? 'This role approves money: their PIN must have 6 digits. If it is shorter, set a new one with Change PIN; until then they cannot approve refunds or voids.'
                  : undefined
            }
          >
            {(id) => (
              <Select
                id={id}
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                data-testid="employee-role"
              >
                <option value="">No role (cannot use this location)</option>
                {props.roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {!employee && (
            <Field
              label="PIN (optional now)"
              help={
                sixDigits
                  ? '6 digits: this role approves money or owner-level actions. Also used to open a session on a tablet.'
                  : '4–6 digits. Staff use it to open a session on a tablet.'
              }
              error={pinError}
            >
              {(id) => (
                <Input
                  id={id}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  className="d-mono"
                  data-testid="employee-pin"
                />
              )}
            </Field>
          )}
          <div className="d-sheet__actions">
            <Button
              type="submit"
              variant="primary"
              busy={busy}
              disabled={!online}
              data-testid="save-employee"
            >
              Save
            </Button>
          </div>
        </form>
        {employee && (
          <section className="d-stack">
            <hr className="d-divider" />
            <h3 className="d-eyebrow">Login (owners &amp; managers)</h3>
            {employee.auth_user_id ? (
              <div className="d-spread">
                <span className="d-muted">A browser login is linked.</span>
                {props.onUnlink && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!online}
                    onClick={() => void props.onUnlink!()}
                  >
                    Unlink login
                  </Button>
                )}
              </div>
            ) : (
              <div className="employees__link">
                <Field label="Login email">
                  {(id) => (
                    <Input
                      id={id}
                      placeholder="email of an existing Supabase Auth user"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="link-email"
                    />
                  )}
                </Field>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!online || !email}
                  onClick={() => void props.onLink?.(email.trim())}
                  data-testid="link-login"
                >
                  Link
                </Button>
              </div>
            )}
            <hr className="d-divider" />
            <h3 className="d-eyebrow">Status</h3>
            <Field label="Reason">
              {(id) => (
                <Input
                  id={id}
                  placeholder="Reason (required for suspend / archive)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="status-reason"
                />
              )}
            </Field>
            <div className="d-actions">
              {props.onSuspend && (
                <Button
                  variant="danger"
                  disabled={!online || !reason.trim()}
                  onClick={() => void props.onSuspend!(reason.trim())}
                  data-testid="suspend-employee"
                >
                  Suspend
                </Button>
              )}
              {props.onReactivate && (
                <Button
                  variant="primary"
                  disabled={!online}
                  onClick={() => void props.onReactivate!()}
                  data-testid="reactivate-employee"
                >
                  Reactivate
                </Button>
              )}
              {props.onArchive && (
                <Button
                  variant="ghost"
                  disabled={!online || !reason.trim()}
                  onClick={() => setConfirmArchive(true)}
                  data-testid="archive-employee"
                >
                  Archive
                </Button>
              )}
            </div>
          </section>
        )}
      </Sheet>
      {employee && props.onArchive && (
        <ConfirmSheet
          open={confirmArchive}
          title={`Archive ${employee.display_name}?`}
          confirmLabel="Archive"
          busy={archiving}
          onClose={() => setConfirmArchive(false)}
          onConfirm={() => {
            setArchiving(true);
            // on success the employee sheet closes with this one; a refusal is toasted
            void props.onArchive!(reason.trim()).catch(() => {
              setArchiving(false);
              setConfirmArchive(false);
            });
          }}
        >
          <p>
            Their PIN is removed and any open tablet session ends. They leave the staff list (Show
            archived still lists them) and their history stays.
          </p>
          <p className="d-muted">An archived employee cannot be brought back from here.</p>
        </ConfirmSheet>
      )}
    </>
  );
}

function PinSheet({
  employee,
  sixDigits,
  onClose,
  onSave,
}: {
  employee: Employee;
  /** The employee approves money or owner-level actions: exactly 6 digits (ADR-023). */
  sixDigits: boolean;
  onClose: () => void;
  onSave: (pin: string) => Promise<void>;
}) {
  const online = useOnline();
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const v = validatePin(pin, { sixDigits });
  const error =
    pin && !v.ok
      ? (v as { error: string }).error
      : pin && confirm && pin !== confirm
        ? 'PINs do not match'
        : null;
  return (
    <Sheet open onClose={onClose} title={`${pinAction(employee)} for ${employee.display_name}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (error || !pin || pin !== confirm) return;
          setBusy(true);
          void onSave(pin).finally(() => setBusy(false));
        }}
        className="d-stack"
      >
        <Field
          label="New PIN"
          help={
            sixDigits
              ? '6 digits, not a simple sequence: this employee approves money or owner-level actions'
              : '4–6 digits, not a simple sequence'
          }
          error={error}
        >
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              autoFocus
              className="d-mono"
              data-testid="pin-new"
            />
          )}
        </Field>
        <Field label="Confirm PIN">
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ''))}
              className="d-mono"
              data-testid="pin-confirm"
            />
          )}
        </Field>
        <p className="d-help">
          PINs are hashed on the server. Nobody can read a PIN back; set a new one if it is
          forgotten.
        </p>
        <div className="d-sheet__actions">
          <Button
            type="submit"
            variant="primary"
            busy={busy}
            disabled={!online || !pin || pin !== confirm || !!error}
            data-testid="pin-save"
          >
            Save PIN
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

/** An employee with a PIN changes it; one without sets their first. */
function pinAction(e: Pick<Employee, 'pin_set_at'>): 'Change PIN' | 'Set PIN' {
  return e.pin_set_at ? 'Change PIN' : 'Set PIN';
}
